import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import NotificationSchedule from "../types/notificationSchedule";
import Option from "../types/option";
import ActiveSchedule from "../types/activeSchedule";

interface ScheduleState {
  status: "idle" | "loading" | "failed";
  scheduledNotifications?: NotificationSchedule[]; //
  selectedSchedule?: NotificationSchedule;
  scheduleTypes?: Option[];
  activeReports?: ActiveSchedule[]; //
}

const initialState: ScheduleState = {
  status: "idle",
};

export const addSchedule = createAsyncThunk<
  NotificationSchedule,
  NotificationSchedule
>("schedule/add", async (data: NotificationSchedule) => {
  const response = await http.post<NotificationSchedule>(`schedule`, data, {
    params: {
      successToast: true,
    },
  });
  return response.data;
});

export const getSchedules = createAsyncThunk<NotificationSchedule[]>(
  "schedule/get-all",
  async () => {
    const response = await http.get<NotificationSchedule[]>("schedule");
    return response.data;
  },
);

export const getSchedule = createAsyncThunk<NotificationSchedule, number>(
  "schedule/get",
  async (id: number) => {
    const response = await http.get<NotificationSchedule>(`schedule/${id}`);
    return response.data;
  },
);

export const editSchedule = createAsyncThunk<
  NotificationSchedule,
  NotificationSchedule
>("schedule/edit", async (data: NotificationSchedule) => {
  const response = await http.put<NotificationSchedule>(
    `schedule/${data.id}`,
    data,
    {
      params: {
        successToast: true,
      },
    },
  );
  return response.data;
});

export const deleteSchedule = createAsyncThunk<null, number>(
  "schedule/delete",
  async (id: number) => {
    const response = await http.delete(`schedule/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const fetchScheduleTypes = createAsyncThunk<Option[]>(
  "types/fetch",
  async () => {
    const response = await http.get<Option[]>("schedule/types");
    return response.data;
  },
);

export const fetchActiveSchedules = createAsyncThunk<ActiveSchedule[]>(
  "schedule/fetch-active",
  async () => {
    const response = await http.get<ActiveSchedule[]>("schedule/active");
    return response.data;
  },
);

export const disableSchedule = createAsyncThunk<null, number>(
  "schedule/disable",
  async (id: number) => {
    const response = await http.delete(`schedule/active/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    clearSelectedSchedule(state) {
      state.selectedSchedule = initialState.selectedSchedule;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addSchedule.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addSchedule.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(addSchedule.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getSchedules.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSchedules.fulfilled, (state, action) => {
        state.status = "idle";
        state.scheduledNotifications = action.payload;
      })
      .addCase(getSchedules.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getSchedule.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSchedule.fulfilled, (state, action) => {
        state.status = "idle";
        state.selectedSchedule = action.payload;
      })
      .addCase(getSchedule.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(deleteSchedule.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchScheduleTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchScheduleTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.scheduleTypes = action.payload;
      })
      .addCase(fetchScheduleTypes.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchActiveSchedules.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchActiveSchedules.fulfilled, (state, action) => {
        state.status = "idle";
        state.activeReports = action.payload;
      })
      .addCase(fetchActiveSchedules.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(disableSchedule.pending, (state) => {
        state.status = "loading";
      })
      .addCase(disableSchedule.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(disableSchedule.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export const { clearSelectedSchedule } = scheduleSlice.actions;

export default scheduleSlice.reducer;
